<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { trans } from '@tools/translation';
import { useSelectionArchiveStore } from '@stores/useSelectionArchiveStore.ts';
import { useLoading } from '@composables/useLoading.ts';
import SliderModal from '@components/SliderModal.vue';
import Typography from '@components/Typography.vue';
import Button from '@components/Button.vue';
import Spinner from '@components/Spinner.vue';
import Panel from '@components/Panel.vue';
import { storeToRefs } from 'pinia';
import { apiRemoveSelectionItem, type RemoveSelectionItemParams } from '@api/selectionItem/apiSelectionItem';
import type { PipedriveUser } from '@models/groups/selectionItem';

interface SelectionItemArchiveModalProps {
  submit?: (params: RemoveSelectionItemParams) => Promise<boolean>;
  accountManager: PipedriveUser | null;
}

const {
  submit = apiRemoveSelectionItem,
  accountManager,
} = defineProps<SelectionItemArchiveModalProps>();

const emit = defineEmits<{(e: 'validated'): void, (e: 'cancel'): void }>();

const selectionArchiveStore = useSelectionArchiveStore();
const {
  isArchiveModalOpen,
  selectionItemIdToArchive,
} = storeToRefs(selectionArchiveStore);

const { closeArchiveModal } = selectionArchiveStore;

const { isLoading, startLoading, stopLoading } = useLoading();

const refuseTextareaContent = ref<string>('');
const checkedReasons = ref<string[]>([]);

const reasons: { choice: string, value: string }[] = [
  { choice: trans('refuse_proposal.price'), value: 'price' },
  { choice: trans('refuse_proposal.location'), value: 'location' },
  { choice: trans('refuse_proposal.space'), value: 'space' },
  { choice: trans('refuse_proposal.style'), value: 'style' },
  { choice: trans('refuse_proposal.typology'), value: 'typology' },
  { choice: trans('refuse_proposal.conditions'), value: 'conditions' },
  { choice: trans('refuse_proposal.already_in_touch'), value: 'already_in_touch' },
  { choice: trans('refuse_proposal.already_rented'), value: 'already_rented' },
];

const isValid = computed<boolean>(() => checkedReasons.value.length > 0 || refuseTextareaContent.value.trim() !== '');

const handleClose = (): void => {
  closeArchiveModal();
  emit('cancel');
};

const handleSubmit = async (): Promise<void> => {
  if (!isValid.value || !selectionItemIdToArchive.value) {
    return;
  }

  startLoading();

  const finalReasons = [ ...checkedReasons.value ];
  if (refuseTextareaContent.value.trim() !== '') {
    finalReasons.push(trans('refuse_proposal.comment') + refuseTextareaContent.value.trim());
  }

  await submit({
    selectionItemId: selectionItemIdToArchive.value,
    refusalReasons: finalReasons,
  });

  stopLoading();

  closeArchiveModal();
  emit('validated');
};

watch(isArchiveModalOpen, (newValue) => {
  if (!newValue) {
    return;
  }

  refuseTextareaContent.value = '';
  checkedReasons.value = [];
});
</script>

<template>
  <SliderModal
    :is-open="isArchiveModalOpen"
    class="archive-selection-modal"
    @close="handleClose"
  >
    <template #header>
      {{ trans('deal_dashboard.action.archive.selection.title') }}
    </template>

    <template #body>
      <div
        :class="[
          'mb-20',
          'flex flex-col gap-y-6',
          'px-6'
        ]"
      >
        <Typography variant="content-heading-2">
          {{ trans('sliders.refuse_proposal.title') }}
        </Typography>

        <Panel
          v-if="accountManager"
          :title="trans('slider.refuse_proposal.panel.title')"
          :content="trans('slider.refuse_proposal.panel.content')"
          :account-manager="accountManager"
          type="info-lightest"
        />

        <div class="flex flex-col gap-y-3">
          <div
            v-for="reason in reasons"
            :key="reason.value"
            class="input-checkbox"
          >
            <input
              :id="`reason_${reason.value}`"
              v-model="checkedReasons"
              type="checkbox"
              :value="reason.choice"
            >

            <label :for="`reason_${reason.value}`">
              <Typography variant="body">
                {{ reason.choice }}
              </Typography>
            </label>
          </div>
        </div>

        <div class="flex flex-col gap-y-2">
          <label for="refuse-comment">
            <Typography variant="body-bold">
              {{ trans('sliders.refuse_proposal.textarea_label') }}
            </Typography>
          </label>

          <div class="input-text">
            <textarea
              id="refuse-comment"
              v-model="refuseTextareaContent"
              :placeholder="trans('sliders.refuse_proposal.textarea_placeholder')"
              class="min-h-24"
            />
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex gap-x-4">
        <Button
          variant="secondary"
          @click="handleClose"
        >
          {{ trans('sliders.refuse_proposal.cta_cancel') }}
        </Button>

        <Button
          variant="primary"
          :disabled="!isValid || isLoading"
          data-cy-ref="archive-validation-button"
          @click="handleSubmit"
        >
          <Spinner v-if="isLoading" />

          <template v-else>
            {{ trans('sliders.refuse_proposal.cta_send') }}
          </template>
        </Button>
      </div>
    </template>
  </SliderModal>
</template>

<style scoped lang="scss">
.archive-selection-modal {
  :deep(.input-text) {
    textarea {
      @apply rounded border border-gray-light focus:border-blue outline-none;

      @apply w-full resize-none;

      @apply p-3;
    }
  }
}
</style>
